/**
 * Custom hook for chat functionality
 * WORLD CLASS with streaming support like ChatGPT
 * NOW WITH DOCUMENT CITATIONS (Saga-style) 🎯
 */
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../../../utils/api';
import type { ChatMessage, Citation } from '../types';

interface UseChatProps {
  user: any;
  allCases: any[];
  language: string;
  t: (en: string, no: string) => string;
  parseMarkdown: (text: string) => string;
}

// Check if a query likely needs document citations
const shouldUseCitations = (question: string): boolean => {
  const citationKeywords = [
    // Norwegian - Document types
    'kontrakt', 'avtale', 'dokument', 'klausul', 'paragraf', 'seksjon',
    'vilkår', 'bestemmelse', 'referanse', 'kilde', 'hva står det',
    'leiekontrakt', 'arbeidsavtale', 'aksjonæravtale', 'oppsigelse',
    'hva sier', 'ifølge', 'basert på',
    // Norwegian - Actions that need document context
    'oppsummer', 'analyser', 'gjennomgå', 'forklar', 'finn', 'søk',
    'les', 'vis', 'hent', 'sjekk', 'fortell om',
    // English - Document types
    'contract', 'agreement', 'document', 'clause', 'section', 
    'terms', 'reference', 'source', 'what does', 'according to',
    'lease', 'employment', 'shareholder',
    // English - Actions
    'summarize', 'summary', 'analyze', 'review', 'find', 'search',
    'read', 'show', 'get', 'check', 'tell me about',
    // File extensions and types
    '.pdf', '.docx', 'fil', 'file'
  ];
  const lowerQ = question.toLowerCase();
  return citationKeywords.some(kw => lowerQ.includes(kw));
};

export const useChat = ({ user, allCases, language, t, parseMarkdown }: UseChatProps) => {
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [briefShown, setBriefShown] = useState(false);
  const [briefCollapsed, setBriefCollapsed] = useState(false);
  const briefShownRef = useRef(false);
  
  // Track conversation ID for context persistence
  const conversationIdRef = useRef<string | null>(null);
  
  // Abort controller for streaming
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleSubmit = useCallback(async (customMessage?: string) => {
    const message = customMessage || chatInput.trim();
    if (!message || isTyping || isStreaming) return;
    
    // Collapse brief when user starts chatting
    if (!briefCollapsed && briefShown) {
      setBriefCollapsed(true);
    }
    
    // Add user message
    setChatHistory(prev => [...prev, { type: 'user', text: message, timestamp: new Date() }]);
    setChatInput('');
    setIsTyping(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // 🎯 Check if we should use citation mode (document-related queries)
      const useCitations = shouldUseCitations(message);
      console.log(`🎯 [useChat] Citation mode: ${useCitations ? 'ON' : 'OFF'} for query: "${message.slice(0, 50)}..."`);
      
      // If citation mode, use STREAMING citation endpoint for fast response
      if (useCitations) {
        setChatHistory(prev => [...prev, { 
          type: 'assistant', 
          text: '',
          timestamp: new Date()
        }]);
        
        setIsTyping(false);
        setIsStreaming(true);
        
        // Create abort controller for citation streaming
        abortControllerRef.current = new AbortController();
        
        try {
          console.log('📚 [useChat] Starting STREAMING citation request...');
          
          // Use native fetch to avoid rrweb interception issues
          const nativeFetch = (window as any).__nativeFetch || fetch;
          
          const response = await nativeFetch(`${API_URL}/ai/query-with-citations/stream`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              query: message,
              include_citations: true,
              max_citations: 5
            }),
            signal: abortControllerRef.current.signal,
          });
          
          if (!response.ok) {
            throw new Error('Streaming citation endpoint not available');
          }
          
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();
          let fullText = '';
          let receivedCitations: Citation[] = [];
          
          if (reader) {
            console.log('📚 [useChat] Got reader, starting to read stream...');
            let chunkCount = 0;
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                console.log(`📚 [useChat] Stream done after ${chunkCount} chunks`);
                break;
              }
              
              const chunk = decoder.decode(value, { stream: true });
              const lines = chunk.split('\n');
              
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  const data = line.slice(6);
                  if (data === '[DONE]') {
                    console.log('📚 [useChat] Received [DONE] signal');
                    break;
                  }
                  
                  try {
                    const parsed = JSON.parse(data);
                    chunkCount++;
                    
                    // Handle citations (sent first)
                    if (parsed.citations) {
                      receivedCitations = parsed.citations;
                      console.log(`📚 [useChat] Received ${receivedCitations.length} citations`);
                      
                      // Update message with citations immediately
                      setChatHistory(prev => {
                        const newHistory = [...prev];
                        const lastIdx = newHistory.length - 1;
                        if (lastIdx >= 0 && newHistory[lastIdx].type === 'assistant') {
                          newHistory[lastIdx] = {
                            ...newHistory[lastIdx],
                            citations: receivedCitations
                          };
                        }
                        return newHistory;
                      });
                    }
                    
                    // Handle streaming content - TRUE real-time streaming
                    if (parsed.content) {
                      fullText += parsed.content;
                      
                      // Log every 10th chunk for debugging
                      if (chunkCount % 10 === 0) {
                        console.log(`📚 [useChat] Chunk ${chunkCount}: "${parsed.content.slice(0, 20)}..."`);
                      }
                      
                      setChatHistory(prev => {
                        const newHistory = [...prev];
                        const lastIdx = newHistory.length - 1;
                        if (lastIdx >= 0 && newHistory[lastIdx].type === 'assistant') {
                          newHistory[lastIdx] = {
                            ...newHistory[lastIdx],
                            text: fullText,
                            citations: receivedCitations
                          };
                        }
                        return newHistory;
                      });
                    }
                    
                    // Handle completion
                    if (parsed.done) {
                      console.log('📚 [useChat] Streaming complete');
                    }
                    
                    // Handle errors
                    if (parsed.error) {
                      throw new Error(parsed.error);
                    }
                  } catch (e) {
                    // Skip non-JSON lines
                  }
                }
              }
            }
          }
          
          setIsStreaming(false);
          return;
          
        } catch (citationError: any) {
          if (citationError.name === 'AbortError') {
            return;
          }
          console.warn('⚠️ [useChat] Citation streaming failed, falling back to standard:', citationError);
          // Reset state before falling through
          setIsStreaming(false);
          setChatHistory(prev => prev.slice(0, -1)); // Remove empty assistant message
          // Fall through to standard streaming
        }
      }
      
      // Standard streaming flow (non-citation queries)
      // First, add empty assistant message for streaming
      if (!useCitations) {
        setChatHistory(prev => [...prev, { 
          type: 'assistant', 
          text: '',
          timestamp: new Date()
        }]);
      }
      
      setIsTyping(false);
      setIsStreaming(true);
      
      // Create abort controller
      abortControllerRef.current = new AbortController();
      
      // Try streaming endpoint first
      try {
        console.log('🚀 [useChat] Starting stream request to:', `${API_URL}/ai/quick-question/stream`);
        const response = await fetch(`${API_URL}/ai/quick-question/stream`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question: message,
            conversation_id: conversationIdRef.current
          }),
          signal: abortControllerRef.current.signal,
        });
        
        console.log('📡 [useChat] Response status:', response.status, 'Content-Type:', response.headers.get('content-type'));
        
        if (!response.ok) {
          console.error('❌ [useChat] Stream response not OK:', response.status);
          throw new Error('Streaming not available');
        }
        
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullText = '';
        const allChunks: string[] = []; // Collect all chunks first
        
        if (reader) {
          // Read all chunks from the stream
          let receivedMetadata: { actions?: any[]; suggest_workspace?: boolean } | null = null;
          
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data === '[DONE]') break;
                
                try {
                  const parsed = JSON.parse(data);
                  
                  // Save conversation ID
                  if (parsed.conversation_id) {
                    conversationIdRef.current = parsed.conversation_id;
                  }
                  
                  // Handle metadata (actions, suggest_workspace)
                  if (parsed.metadata) {
                    receivedMetadata = parsed.metadata;
                    console.log('📦 [useChat] Received metadata:', receivedMetadata);
                  }
                  
                  if (parsed.content) {
                    allChunks.push(parsed.content);
                  }
                } catch (e) {
                  // Skip non-JSON lines
                }
              }
            }
          }
          
          // Now animate the chunks one by one - ChatGPT style typing effect
          console.log(`🎬 [useChat] Animating ${allChunks.length} chunks...`);
          
          for (let i = 0; i < allChunks.length; i++) {
            fullText += allChunks[i];
            
            // Update UI with current text
            setChatHistory(prev => {
              const newHistory = [...prev];
              const lastIdx = newHistory.length - 1;
              if (lastIdx >= 0 && newHistory[lastIdx].type === 'assistant') {
                newHistory[lastIdx] = {
                  ...newHistory[lastIdx],
                  text: fullText
                };
              }
              return newHistory;
            });
            
            // Add delay for typing effect - faster for punctuation, slower for words
            const content = allChunks[i];
            const isPunctuation = /^[.,!?;:\s]+$/.test(content);
            const delay = isPunctuation ? 5 : Math.min(25, 10 + content.length * 2);
            await new Promise(r => setTimeout(r, delay));
          }
          
          // After animation, add actions and suggest_workspace if present
          if (receivedMetadata) {
            setChatHistory(prev => {
              const newHistory = [...prev];
              const lastIdx = newHistory.length - 1;
              if (lastIdx >= 0 && newHistory[lastIdx].type === 'assistant') {
                newHistory[lastIdx] = {
                  ...newHistory[lastIdx],
                  actions: receivedMetadata?.actions || [],
                  suggestWorkspace: receivedMetadata?.suggest_workspace || false
                };
              }
              return newHistory;
            });
          }
        }
        
      } catch (streamError) {
        // Fallback to non-streaming endpoint
        console.error('⚠️ [useChat] Streaming failed, falling back to non-streaming:', streamError);
        
        // Remove empty streaming message
        setChatHistory(prev => prev.slice(0, -1));
        setIsTyping(true);
        setIsStreaming(false);
        
        const res = await axios.post(`${API_URL}/ai/quick-question`, { 
          question: message,
          conversation_id: conversationIdRef.current
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        // Store conversation ID
        if (res.data.conversation_id) {
          conversationIdRef.current = res.data.conversation_id;
        }
        
        // Simulate streaming for better UX
        const answer = res.data.answer || t('I could not answer that.', 'Jeg kunne ikke svare på det.');
        const words = answer.split(' ');
        let currentText = '';
        
        // Add assistant message
        setChatHistory(prev => [...prev, { 
          type: 'assistant', 
          text: '',
          timestamp: new Date()
        }]);
        
        setIsTyping(false);
        setIsStreaming(true);
        
        // Stream words with delay
        for (let i = 0; i < words.length; i++) {
          currentText += (i > 0 ? ' ' : '') + words[i];
          const textToShow = currentText;
          
          setChatHistory(prev => {
            const newHistory = [...prev];
            const lastIdx = newHistory.length - 1;
            if (lastIdx >= 0 && newHistory[lastIdx].type === 'assistant') {
              newHistory[lastIdx] = {
                ...newHistory[lastIdx],
                text: textToShow
              };
            }
            return newHistory;
          });
          
          // Variable delay for natural feel (faster for short words)
          await new Promise(r => setTimeout(r, Math.min(30, 15 + words[i].length * 2)));
        }
      }
      
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // User cancelled - do nothing
        return;
      }
      
      setChatHistory(prev => {
        const newHistory = [...prev];
        const lastIdx = newHistory.length - 1;
        if (lastIdx >= 0 && newHistory[lastIdx].type === 'assistant' && !newHistory[lastIdx].text) {
          // Replace empty streaming message with error
          newHistory[lastIdx] = {
            ...newHistory[lastIdx],
            text: t('Something went wrong. Please try again.', 'Noe gikk galt. Prøv igjen.')
          };
        } else {
          // Add error message
          newHistory.push({ 
            type: 'assistant', 
            text: t('Something went wrong. Please try again.', 'Noe gikk galt. Prøv igjen.'),
            timestamp: new Date()
          });
        }
        return newHistory;
      });
      // Check if we should suggest workspace (after 4+ user messages)
      const userMessageCount = chatHistory.filter(m => m.type === 'user').length + 1;
      if (userMessageCount >= 3) {
        // Add suggestWorkspace flag to the last assistant message
        setChatHistory(prev => {
          const newHistory = [...prev];
          const lastIdx = newHistory.length - 1;
          if (lastIdx >= 0 && newHistory[lastIdx].type === 'assistant') {
            newHistory[lastIdx] = {
              ...newHistory[lastIdx],
              suggestWorkspace: true
            };
          }
          return newHistory;
        });
      }
      
    } finally {
      setIsTyping(false);
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [chatInput, isTyping, isStreaming, briefCollapsed, briefShown, chatHistory, t]);

  const addBriefMessage = useCallback((message: ChatMessage) => {
    setChatHistory([message]);
    setBriefShown(true);
  }, []);

  // Stop streaming
  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Reset conversation (start fresh)
  const resetConversation = useCallback(() => {
    stopStreaming();
    conversationIdRef.current = null;
    setChatHistory([]);
    setBriefShown(false);
    setBriefCollapsed(false);
  }, [stopStreaming]);

  return {
    chatInput,
    setChatInput,
    chatHistory,
    setChatHistory,
    isTyping,
    isStreaming,
    briefShown,
    setBriefShown,
    briefCollapsed,
    setBriefCollapsed,
    briefShownRef,
    handleSubmit,
    addBriefMessage,
    resetConversation,
    stopStreaming,
    conversationId: conversationIdRef.current,
  };
};
